import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { gymAPI } from '../lib/api';
import { useRabel } from './gymRabel';
import { BTN, INPUT, Header, NotForYou, ago, setsIn, orderOf, newId } from './Gym';

/**
 * The gym's home: two pages to swipe between.
 *
 *   Categories  a tile per muscle group ("Bum", "Legs"…): tap one for its
 *               sessions, for when you want to train but lack inspiration.
 *   Custom      your own days (Day 1, Day 2…), mixed however you like.
 *
 * A session's category is a plain name; empty is a custom day. Lists are in an
 * order you set (Reorder), shared by both people.
 */

export const CATEGORIES = [
  { name: 'Bum', emoji: '🍑' },
  { name: 'Legs', emoji: '🦵' },
  { name: 'Shoulders', emoji: '🏋️' },
  { name: 'Arms', emoji: '💪' },
  { name: 'Chest', emoji: '🫀' },
  { name: 'Back', emoji: '🪽' },
  { name: 'Core', emoji: '🧱' },
  { name: 'Full body', emoji: '⚡' },
  { name: 'Mobility', emoji: '🧘' },
  { name: 'Cardio', emoji: '❤️‍🔥' },
];
const sameName = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
export const emojiFor = (name) => CATEGORIES.find(c => sameName(c.name, name))?.emoji || '🏷️';
/** The built-in categories, then any made up along the way. */
export function allCategories(sessions = []) {
  const extra = [...new Set(sessions.map(s => s.category).filter(Boolean))].filter(c => !CATEGORIES.some(k => sameName(k.name, c)));
  return [...CATEGORIES.map(c => c.name), ...extra.sort()];
}

function useSessions() {
  const [sessions, setSessions] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  useEffect(() => {
    gymAPI.sessions().then(r => setSessions(r.data)).catch(err => { if (err.response?.status === 404) setForbidden(true); else setSessions([]); });
  }, []);
  return { sessions, setSessions, forbidden };
}

/** A list of sessions, in their order; Reorder shows handles to move them. */
export function SessionCards({ sessions, setSessions, empty }) {
  const [reordering, setReordering] = useState(false);
  const [failed, setFailed] = useState(false);
  if (!sessions.length) return <p className="text-gray-500 text-sm py-2">{empty}</p>;

  function move(i, d) {
    const j = i + d;
    if (j < 0 || j >= sessions.length) return;
    const before = sessions;
    const list = sessions.slice();
    [list[i], list[j]] = [list[j], list[i]];
    setSessions(list);
    setFailed(false);
    // Put it back if it didn't save, so the screen never shows an order that isn't kept.
    gymAPI.orderSessions(list.map(s => s.id)).catch(() => { setSessions(before); setFailed(true); });
  }

  return (
    <>
      {failed && <p className="text-sm text-red-400 mb-1">Couldn't save that order. Check your signal and try again.</p>}
      {sessions.length > 1 && (
        <div className="flex justify-end -mt-1 mb-1">
          <button onClick={() => setReordering(r => !r)} className="text-xs text-gray-400 px-2 py-1">{reordering ? 'Done' : 'Reorder'}</button>
        </div>
      )}
      <div className="space-y-2">
        {sessions.map((s, i) => (
          <div key={s.id} className="flex items-stretch gap-2">
            <Link to={`/gym/s/${s.id}`} className="flex-1 min-w-0 block rounded-2xl bg-[#141414] border border-[#232323] px-4 py-3 active:bg-[#1a1a1a]">
              <div className="flex items-baseline gap-2">
                <span className="text-lg font-semibold flex-1 truncate">{s.name}</span>
                <span className="text-xs text-gray-500">{ago(s.last_done_at)}</span>
              </div>
              <div className="text-sm text-gray-400 truncate">{(s.exercises || []).map(e => e.name).join(', ') || 'No exercises yet'}</div>
              <div className="text-xs text-gray-600 mt-0.5">{(s.exercises || []).length} exercises · {setsIn(s.exercises)} sets{s.created_by_name ? ` · ${s.created_by_name}'s` : ''}</div>
            </Link>
            {reordering && (
              <div className="flex flex-col justify-center gap-1">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="w-11 h-11 rounded-xl bg-[#141414] border border-[#2a2a2a] text-gray-300 disabled:opacity-25" aria-label="Up">▲</button>
                <button onClick={() => move(i, 1)} disabled={i === sessions.length - 1} className="w-11 h-11 rounded-xl bg-[#141414] border border-[#2a2a2a] text-gray-300 disabled:opacity-25" aria-label="Down">▼</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

/** "+ New …": a name, then straight into the editor. */
function NewSession({ category = '', label, suggest }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  async function create() {
    const value = name.trim() || suggest;
    if (!value) return;
    const r = await gymAPI.createSession({ name: value, category, exercises: [] });
    navigate(`/gym/s/${r.data.id}`);
  }
  if (!open) {
    return <button onClick={() => { setName(''); setOpen(true); }} className={`${BTN} w-full mt-3 bg-[#141414] border border-dashed border-[#333] text-green-400`}>{label}</button>;
  }
  return (
    <div className="flex gap-2 mt-3">
      <input autoFocus value={name} onChange={e => setName(e.target.value)} onKeyDown={e => e.key === 'Enter' && create()} placeholder={suggest || 'Session name'} className={INPUT} />
      <button onClick={create} className={`${BTN} bg-green-600 text-black`}>Make</button>
    </div>
  );
}

/** The home screen. */
export function GymHome() {
  const { say } = useRabel();
  const { sessions, setSessions, forbidden } = useSessions();
  const [active, setActive] = useState(null);
  const [page, setPage] = useState(() => { try { return Number(sessionStorage.getItem('gym_home_page')) || 0; } catch { return 0; } });
  const pager = useRef(null);

  useEffect(() => { gymAPI.activeRun().then(r => setActive(r.data)).catch(() => {}); }, []);
  // Back to the page you were on.
  useEffect(() => {
    const el = pager.current;
    if (el && sessions) el.scrollTo({ left: page * el.clientWidth, behavior: 'instant' });
  }, [sessions]); // eslint-disable-line react-hooks/exhaustive-deps

  if (forbidden) return <NotForYou />;

  const go = (n) => {
    setPage(n);
    try { sessionStorage.setItem('gym_home_page', String(n)); } catch { /* fine */ }
    pager.current?.scrollTo({ left: n * pager.current.clientWidth, behavior: 'smooth' });
  };
  const onScroll = (e) => {
    const n = Math.round(e.currentTarget.scrollLeft / e.currentTarget.clientWidth);
    if (n !== page) { setPage(n); try { sessionStorage.setItem('gym_home_page', String(n)); } catch { /* fine */ } }
  };

  const custom = (sessions || []).filter(s => !s.category);
  const counts = Object.fromEntries(allCategories(sessions || []).map(c => [c, (sessions || []).filter(s => sameName(s.category, c)).length]));
  const tiles = allCategories(sessions || []).sort((a, b) => (counts[b] > 0) - (counts[a] > 0));

  return (
    <>
      {active && (
        <Link to="/gym/workout" className="block mb-4 rounded-2xl bg-green-600/15 border border-green-700 px-4 py-3">
          <div className="text-xs uppercase tracking-wide text-green-400">{say('Workout in progress', 'You were mid-slay 💅')}</div>
          <div className="text-lg font-semibold">{active.name}</div>
          <div className="text-sm text-gray-400">{Object.keys(active.state?.done || {}).length} of {setsIn(active.state?.exercises)} sets · carry on</div>
        </Link>
      )}

      <div className="flex rounded-full bg-[#141414] border border-[#232323] p-1 mb-3">
        {['Categories', 'Custom'].map((label, n) => (
          <button key={label} onClick={() => go(n)} className={`flex-1 rounded-full py-2 text-sm font-semibold transition ${page === n ? 'bg-green-500 text-black' : 'text-gray-400'}`}>{label}</button>
        ))}
      </div>

      {sessions === null ? <p className="text-gray-500">Loading…</p> : (
        <div ref={pager} onScroll={onScroll} className="flex overflow-x-auto snap-x snap-mandatory -mx-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <section className="w-full flex-shrink-0 snap-start px-4">
            <p className="text-xs text-gray-500 mb-2">{say('Pick a muscle group and choose a session from it.', 'What are we sculpting today? 💅')}</p>
            <div className="grid grid-cols-2 gap-2">
              {tiles.map(c => (
                <Link key={c} to={`/gym/c/${encodeURIComponent(c)}`} className={`rounded-2xl bg-[#141414] border border-[#232323] px-3 py-4 active:bg-[#1a1a1a] ${counts[c] ? '' : 'opacity-50'}`}>
                  <div className="text-3xl">{emojiFor(c)}</div>
                  <div className="font-semibold mt-1">{c}</div>
                  <div className="text-xs text-gray-500">{counts[c] ? `${counts[c]} session${counts[c] === 1 ? '' : 's'}` : 'None yet'}</div>
                </Link>
              ))}
            </div>
          </section>
          <section className="w-full flex-shrink-0 snap-start px-4">
            <p className="text-xs text-gray-500 mb-2">Your own days: mix a session with extras, in your order.</p>
            <SessionCards sessions={custom} setSessions={(list) => setSessions(all => [...list, ...all.filter(s => s.category)])} empty="No custom days yet." />
            <NewSession label={say('+ New day', '+ New day, let\'s go 💖')} suggest={`Day ${custom.length + 1}`} />
          </section>
        </div>
      )}
    </>
  );
}

/** One category's sessions. */
export function CategoryPage() {
  const { name } = useParams();
  const { sessions, setSessions, forbidden } = useSessions();
  if (forbidden) return <NotForYou />;
  const mine = (sessions || []).filter(s => sameName(s.category, name));
  return (
    <>
      <Header back="/gym" title={<span>{emojiFor(name)} {name}</span>} />
      {sessions === null ? <p className="text-gray-500">Loading…</p> : (
        <>
          <SessionCards
            sessions={mine}
            setSessions={(list) => setSessions(all => [...list, ...all.filter(s => !sameName(s.category, name))])}
            empty={`No ${name.toLowerCase()} sessions yet.`}
          />
          <NewSession category={name} label={`+ New ${name.toLowerCase()} session`} suggest={`${name} ${mine.length + 1}`} />
        </>
      )}
    </>
  );
}

/** The editor's category choice: a custom day, a built-in one, or a new one. */
export function CategoryPicker({ value, onChange, known = [] }) {
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState('');
  const options = [...new Set([...CATEGORIES.map(c => c.name), ...known.filter(Boolean), ...(value ? [value] : [])])];
  const chip = (active) => `flex-shrink-0 rounded-full px-3 py-1.5 text-sm border ${active ? 'bg-green-500 border-green-500 text-black font-semibold' : 'bg-[#141414] border-[#2a2a2a] text-gray-300'}`;
  return (
    <div className="flex gap-1.5 overflow-x-auto pb-1 mb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <button onClick={() => onChange('')} className={chip(!value)}>Custom day</button>
      {options.map(c => <button key={c} onClick={() => onChange(c)} className={chip(sameName(value, c))}>{emojiFor(c)} {c}</button>)}
      {adding
        ? (
          <span className="flex-shrink-0 flex gap-1">
            <input autoFocus value={text} onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && text.trim()) { onChange(text.trim()); setAdding(false); } }} placeholder="New category" className="bg-[#141414] border border-[#2a2a2a] rounded-full px-3 py-1 text-sm text-white w-36 focus:outline-none" />
            <button onClick={() => { if (text.trim()) onChange(text.trim()); setAdding(false); }} className={chip(false)}>Add</button>
          </span>
        )
        : <button onClick={() => { setText(''); setAdding(true); }} className={chip(false)}>+ New</button>}
    </div>
  );
}

/** Copy another session's exercises in: bum, plus some mobility, plus a random accessory. */
export function AddFromSession({ currentId, onAdd, onCancel }) {
  const [sessions, setSessions] = useState(null);
  useEffect(() => { gymAPI.sessions().then(r => setSessions(r.data.filter(s => s.id !== currentId && (s.exercises || []).length))).catch(() => setSessions([])); }, [currentId]);
  function add(s) {
    // New ids throughout, a circuit kept together under a new id.
    const circuits = new Map();
    onAdd((s.exercises || []).map(ex => ({
      ...ex,
      id: newId(),
      ...(ex.circuit ? { circuit: circuits.get(ex.circuit) || circuits.set(ex.circuit, newId()).get(ex.circuit) } : {}),
      sets: ex.sets.map(st => ({ ...st, id: newId() })),
    })));
  }
  return (
    <div className="mt-3 rounded-2xl bg-[#141414] border border-green-800 p-3">
      <div className="flex items-center mb-2">
        <span className="font-semibold flex-1">Add everything from a session</span>
        <button onClick={onCancel} className="text-sm text-gray-400 px-2">Cancel</button>
      </div>
      {sessions === null && <p className="text-sm text-gray-500">Loading…</p>}
      {sessions?.length === 0 && <p className="text-sm text-gray-500">No other sessions with exercises yet.</p>}
      <div className="space-y-1.5 max-h-80 overflow-y-auto">
        {sessions?.map(s => (
          <button key={s.id} onClick={() => add(s)} className="w-full text-left rounded-xl border border-[#2a2a2a] px-3 py-2 active:bg-[#1a1a1a]">
            <div className="font-semibold">{s.category ? `${emojiFor(s.category)} ` : ''}{s.name}</div>
            <div className="text-xs text-gray-500 truncate">{s.exercises.map(e => e.name).join(', ')}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// --- Add to calendar ----------------------------------------------------------------------

const icsDate = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
const icsText = (s) => String(s).replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');

/** About how long a session takes: 40 s a set plus its rests, to the nearest 5 minutes. */
export function estimateMinutes(exercises = []) {
  const seconds = orderOf(exercises).reduce((n, o) => n + 40 + (o.s.rest || 0), 0);
  return Math.max(15, Math.round(seconds / 60 / 5) * 5);
}

/** Put a session in the phone's calendar (iPhone, Outlook) or Google Calendar, at a date and time. */
export function AddToCalendar({ session, onClose }) {
  const today = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const [date, setDate] = useState(`${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`);
  const [time, setTime] = useState('18:00');
  const [minutes, setMinutes] = useState(estimateMinutes(session.exercises));

  const start = new Date(`${date}T${time}:00`);
  const end = new Date(start.getTime() + minutes * 60000);
  const title = `Gym: ${session.name}`;
  const link = `${window.location.origin}/gym/s/${session.id}`;
  const details = [...(session.exercises || []).map(e => `${e.name}: ${e.sets.length} × ${e.sets[0]?.reps ?? '-'}`), '', link].join('\n');
  const valid = !Number.isNaN(start.getTime());

  function phoneCalendar() {
    const ics = [
      'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Mission Control//Gym//EN', 'BEGIN:VEVENT',
      `UID:${newId()}@missioncontrol.show`, `DTSTAMP:${icsDate(new Date())}`, `DTSTART:${icsDate(start)}`, `DTEND:${icsDate(end)}`,
      `SUMMARY:${icsText(title)}`, `DESCRIPTION:${icsText(details)}`, `URL:${link}`,
      'BEGIN:VALARM', 'TRIGGER:-PT30M', 'ACTION:DISPLAY', `DESCRIPTION:${icsText(title)}`, 'END:VALARM',
      'END:VEVENT', 'END:VCALENDAR',
    ].join('\r\n');
    const a = document.createElement('a');
    a.href = `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
    a.download = `${session.name.replace(/[^\w -]/g, '')}.ics`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  const google = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${icsDate(start)}/${icsDate(end)}&details=${encodeURIComponent(details)}`;

  return (
    <div className="mt-3 rounded-2xl bg-[#141414] border border-green-800 p-4 space-y-3">
      <div className="flex items-center">
        <span className="font-semibold flex-1">📅 Add to calendar</span>
        <button onClick={onClose} className="text-sm text-gray-400 px-2">Close</button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <label className="block col-span-1"><span className="block text-[11px] uppercase tracking-wide text-gray-500 mb-1">Day</span><input type="date" value={date} onChange={e => setDate(e.target.value)} className={INPUT} /></label>
        <label className="block"><span className="block text-[11px] uppercase tracking-wide text-gray-500 mb-1">Time</span><input type="time" value={time} onChange={e => setTime(e.target.value)} className={INPUT} /></label>
        <label className="block"><span className="block text-[11px] uppercase tracking-wide text-gray-500 mb-1">Minutes</span><input inputMode="numeric" value={minutes} onChange={e => setMinutes(Number(e.target.value.replace(/\D/g, '')) || 0)} className={INPUT} /></label>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button disabled={!valid} onClick={phoneCalendar} className={`${BTN} bg-green-500 text-black text-sm`}>Phone calendar</button>
        <a href={valid ? google : undefined} target="_blank" rel="noreferrer" className={`${BTN} bg-[#1f1f1f] text-white text-sm text-center`}>Google Calendar</a>
      </div>
      <p className="text-[11px] text-gray-500">Phone calendar works for iPhone, Mac and Outlook, with a reminder 30 minutes before. The event links back here.</p>
    </div>
  );
}
